import {
    PasswordInput,
    TextInput,
} from '@mantine/core';
import { Modal } from '@carbon/react';
import { defineMessages, useIntl } from 'react-intl';
import { useState } from 'react';

import styles from './PasswordChangeModal.module.scss';

import sharedMessages from 'Components/utils/sharedMessages';
import { PASSWORD_REGEX } from 'utils/constants';
import { useConfirmPasswordChangeMutation, useRequestPasswordChangeMutation } from 'api/accountEndpoints';

type Props = {
    setModalOpen: (value: boolean) => void;
    modalOpen: boolean;
}

const PasswordChangeModal = ({
    setModalOpen,
    modalOpen,
}: Props) => {
    const intl = useIntl();
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [repeatedPassword, setRepeatedPassword] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [awaitingCode, setAwaitingCode] = useState(false);
    const [codeError, setCodeError] = useState('');

    const [requestPasswordChange, { isLoading: isRequesting }] = useRequestPasswordChangeMutation();
    const [confirmPasswordChange, { isLoading: isConfirming }] = useConfirmPasswordChangeMutation();

    const isLoading = isRequesting || isConfirming;

    const isIncorrectPassword = newPassword.length >= 8 && !PASSWORD_REGEX.test(newPassword);
    const isPasswordMismatch = repeatedPassword.length > 0 && repeatedPassword !== newPassword;
    const isSubmitDisabled = !currentPassword || !newPassword || !repeatedPassword
        || isIncorrectPassword || isPasswordMismatch || isLoading;

    const reset = () => {
        setCurrentPassword('');
        setNewPassword('');
        setRepeatedPassword('');
        setVerificationCode('');
        setAwaitingCode(false);
        setCodeError('');
    };

    const onModalClose = () => {
        setModalOpen(false);
        reset();
    };

    const onSubmit = async () => {
        if (!awaitingCode) {
            await requestPasswordChange({ currentPassword, newPassword })
                .unwrap()
                .then(() => setAwaitingCode(true))
                .catch(() => { /* handled in rtk query extraOptions */ });
        } else {
            await confirmPasswordChange({ code: verificationCode })
                .unwrap()
                .then(() => {
                    setModalOpen(false);
                    reset();
                })
                .catch(() => {
                    setCodeError(intl.formatMessage(messages.invalidCode));
                });
        }
    };

    return (
        <Modal
            preventCloseOnClickOutside
            open={modalOpen}
            onRequestClose={onModalClose}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onRequestSubmit={onSubmit}
            danger
            modalHeading={intl.formatMessage(messages.changePassword)}
            modalLabel={intl.formatMessage(messages.changePasswordHeading)}
            primaryButtonText={awaitingCode
                ? intl.formatMessage(messages.confirm)
                : intl.formatMessage(sharedMessages.save)}
            primaryButtonDisabled={awaitingCode ? !verificationCode || isLoading : isSubmitDisabled}
            closeButtonLabel={intl.formatMessage(sharedMessages.cancel)}
            secondaryButtonText={intl.formatMessage(sharedMessages.back)}
        >
            {!awaitingCode ? (
                <>
                    <PasswordInput
                        id="current-password-input-in-settings"
                        label={intl.formatMessage(messages.currentPassword)}
                        onChange={(e) => { setCurrentPassword(e.target.value); }}
                        value={currentPassword}
                        size="md"
                    />
                    <PasswordInput
                        id="password-input-in-settings"
                        className={styles.input}
                        label={intl.formatMessage(messages.newPassword)}
                        error={isIncorrectPassword ? intl.formatMessage(messages.newPasswordWarning) : undefined}
                        onChange={(e) => { setNewPassword(e.target.value); }}
                        value={newPassword}
                        size="md"
                    />
                    <PasswordInput
                        id="repeat-password-input-in-settings"
                        className={styles.input}
                        error={isPasswordMismatch ? intl.formatMessage(messages.repeatedPasswordIsDifferent) : undefined}
                        label={intl.formatMessage(messages.repeatPassword)}
                        onChange={(e) => { setRepeatedPassword(e.target.value); }}
                        value={repeatedPassword}
                        size="md"
                    />
                </>
            ) : (
                <>
                    <p className={styles.codeInfo}>
                        {intl.formatMessage(messages.codeSentInfo)}
                    </p>
                    <TextInput
                        id="verification-code-input-in-settings"
                        label={intl.formatMessage(messages.verificationCode)}
                        error={codeError || undefined}
                        onChange={(e) => {
                            setCodeError('');
                            setVerificationCode(e.target.value);
                        }}
                        value={verificationCode}
                        size="md"
                    />
                </>
            )}
        </Modal>
    );
};

const messages = defineMessages({
    changePassword: {
        id: 'src.Screens.Dashboard.Settings.Options.PasswordChangeButton.PasswordChangeModal.changePassword',
        defaultMessage: 'Czy na pewno chcesz zmienić hasło? Ta operacja jest nieodwracalna.',
    },
    changePasswordHeading: {
        id: 'src.Screens.Dashboard.Settings.Options.PasswordChangeButton.PasswordChangeModal.changePasswordHeading',
        defaultMessage: 'Zmiana hasła.',
    },
    currentPassword: {
        id: 'src.Screens.Dashboard.Settings.Options.PasswordChangeButton.PasswordChangeModal.currentPassword',
        defaultMessage: 'Aktualne hasło',
    },
    newPassword: {
        id: 'src.Screens.Dashboard.Settings.Options.PasswordChangeButton.PasswordChangeModal.newPassword',
        defaultMessage: 'Nowe hasło (minimalnie 8 znaków)',
    },
    repeatPassword: {
        id: 'src.Screens.Dashboard.Settings.Options.PasswordChangeButton.PasswordChangeModal.repeatPassword',
        defaultMessage: 'Powtórz hasło',
    },
    newPasswordWarning: {
        id: 'src.Screens.Dashboard.Settings.Options.PasswordChangeButton.PasswordChangeModal.newPasswordWarning',
        defaultMessage: 'Niepoprawne hasło.',
    },
    repeatedPasswordIsDifferent: {
        id: 'src.Screens.Dashboard.Settings.Options.PasswordChangeButton.PasswordChangeModal.repeatedPasswordIsDifferent',
        defaultMessage: 'Hasła nie są takie same.',
    },
    codeSentInfo: {
        id: 'src.Screens.Dashboard.Settings.Options.PasswordChangeButton.PasswordChangeModal.codeSentInfo',
        defaultMessage: 'Kod potwierdzający został wysłany na Twój adres e-mail. Wprowadź go poniżej, aby dokończyć zmianę hasła.',
    },
    verificationCode: {
        id: 'src.Screens.Dashboard.Settings.Options.PasswordChangeButton.PasswordChangeModal.verificationCode',
        defaultMessage: 'Kod potwierdzający',
    },
    invalidCode: {
        id: 'src.Screens.Dashboard.Settings.Options.PasswordChangeButton.PasswordChangeModal.invalidCode',
        defaultMessage: 'Nieprawidłowy kod. Sprawdź i spróbuj ponownie.',
    },
    confirm: {
        id: 'src.Screens.Dashboard.Settings.Options.PasswordChangeButton.PasswordChangeModal.confirm',
        defaultMessage: 'Zatwierdź',
    },
});

export default PasswordChangeModal;
